import { mdiOpenInNew } from '@mdi/js';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    timelineItemClasses,
    TimelineSeparator,
} from '@mui/lab';
import { Button, ButtonGroup, Stack, SxProps, Typography } from '@mui/material';
import MD5 from 'crypto-js/md5';
import React, { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { viewNameDocNumber } from '../../../Constants';
import {
    getProjectDocumentHistory,
    IDocumentVersionHistoryEntry,
} from '../../../redux/project/documents/sliceProjectDocumentHistory';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, bodyFont, vars } from '../../../Theme';
import AppIcon from '../Icons/AppIcon';
import DocStatusIcon from '../Icons/DocStatusIcon';
import DateTag from '../TextBlocks/DateTag';
import MainField from '../TextBlocks/MainField';
import Username from '../TextBlocks/Username';
import LoadingData from './Components/LoadingData';
import PlaceholderCard from './Components/PlaceholderCard';

/**
 * Sidepanel that shows history information on a given document.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });

    /** Allow component to change the URL. */
    const navigate = useNavigate();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Get relevant values from Redux store. */
    const views = useSelector((store: IAppReduxStore) => store.projectViews.data);
    const { data, targetDocId: universalId } = useSelector(
        (store: IAppReduxStore) => store.projectSidepanel,
    );
    const { data: docHistory, loading } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentHistory,
    );

    /**
     * Effect that fetches the relevant document data for displaying in the sidepanel.
     */
    useEffect(() => {
        universalId && dispatch(getProjectDocumentHistory({ universalId, replicaId }));
    }, [universalId, data]);

    /** Sort versions by creation date (newest > oldest). */
    const docs = [...docHistory]
        .sort((a, b) => b.creationDate - a.creationDate)
        .filter(a => Boolean(a.docIndex));

    /**
     * Creates the button for jumping directly to this document in the docNumber view, if it is configured for this project.
     */
    const getLinkButton = () => {
        const view = views[viewNameDocNumber];

        /** Data not yet loaded or project does not have a docNumber view - stop here. */
        if (!docHistory.length || !view) {
            return <></>;
        }

        /** Create the URL that will take the user to the docNumber view.  */
        const btnUrl = `/project/${replicaId}/${view.areaId}/${view.viewId}/${MD5(
            docHistory[0].docNumber,
        )}`;

        return (
            <Stack sx={{ pt: 2 }}>
                <ButtonGroup variant='contained' sx={{ flex: 0, alignSelf: 'center' }}>
                    <Button
                        onClick={() => navigate(btnUrl)}
                        sx={{
                            ...headingFontBold,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            color: vars.white,
                        }}
                    >
                        {t('MoreDetails')}
                    </Button>

                    <Button
                        size='small'
                        onClick={() => window.open(`${window.location.origin}${btnUrl}`, '_blank')}
                    >
                        <AppIcon
                            path={mdiOpenInNew}
                            size={0.8}
                            color={vars.white}
                            tooltipText={t('OpenInNewTab')}
                            tooltipPlacement='top-start'
                        />
                    </Button>
                </ButtonGroup>
            </Stack>
        );
    };

    /**
     * Creates the metadata display for a given document version.
     * @param doc - Target document version.
     */
    const getContent = (doc: IDocumentVersionHistoryEntry) => (
        <Stack sx={{ ...headingFontBold, fontSize: 14, flexGrow: 4, pt: 2 }}>
            <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
                <Stack sx={{ overflow: 'hidden', flex: 1 }}>
                    <MainField text={`${doc.docNumber} - ${doc.docIndex}`} />
                </Stack>

                <DateTag date={doc.creationDate} />
            </Stack>

            <Stack
                direction='row'
                spacing={0.25}
                sx={{
                    alignItems: 'center',
                }}
            >
                <DocStatusIcon
                    type={doc.docStatus}
                    document={doc}
                    tooltip
                    tooltipPlacement='top-start'
                />

                <Username username={doc.creator} />
            </Stack>

            <Stack sx={{ fontSize: 12, ...bodyFont }}>{doc.wfTask}</Stack>
        </Stack>
    );
    /**
     * Creates the styles for the timeline dot.
     * @param newest - Flag indicating if the timeline dot is for the latest version.
     */
    const getDotSx = (newest: boolean): SxProps => ({
        border: `3px solid ${newest ? vars.brandGreen : vars.concrete}`,
        justifyContent: 'center',
        borderRadius: '100%',
        alignItems: 'center',
        bgcolor: vars.white,
        alignSelf: 'start',
        color: vars.font,
        ...headingFontBold,
        flexShrink: 0,
        fontSize: 16,
        flexGrow: 0,
        height: 40,
        width: 40,
    });

    return (
        <Stack sx={{ overflow: 'auto', px: 1 }}>
            <Typography
                variant='h6'
                sx={{
                    fontSize: 16,
                    py: 1,
                }}
            >
                {t('IndexHistory')}
            </Typography>
            {loading && <LoadingData />}
            {!loading && !docs.length && <PlaceholderCard translationKey='NoHistoryPlaceholder' />}
            {docs.length > 0 && (
                <>
                    <Timeline
                        sx={{
                            [`& .${timelineItemClasses.missingOppositeContent}:before`]: {
                                display: 'none',
                            },
                            p: 0,
                            m: 0,
                        }}
                    >
                        {docs?.map((x, i) => (
                            <TimelineItem key={`history_${x.UniversalID}`}>
                                <TimelineSeparator>
                                    <TimelineDot sx={getDotSx(!i)}>{x.docIndex}</TimelineDot>

                                    {i + 1 !== docs.length && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ pl: 0.5, pr: 0, overflow: 'hidden' }}>
                                    {getContent(x)}
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                    {getLinkButton()}
                </>
            )}
        </Stack>
    );
};
